/**
 * @file Allow using app feature flags
 */
import environment from '@@/bits/environment'
import window from '@@/bits/global'
import { isStringTruthy } from '@@/bits/truthy'

type CurrentFlags =
  | 'banksy'
  | 'banksyDev'
  | 'debugFrontend'
  | 'goldPlansVisible'
  | 'managePeopleV2'
  | 'aiImageSearch'
  | 'googleDrive'
  | 'realtimeFetching'
  | 'brahmsCheck'
  | 'surfaceGroupByPanel'
  | 'reportButton'
  | 'magicPadlet'
  | 'zoom'
  | 'spreadsheetExportsV2'
  | 'remakeSettingV2'
  | 'showCommandPalette'
  | 'publicApi'
  | 'autoContentModeration'
  | 'changeEmailVerification'
  | 'loginEmailVerificationWarning'
  | 'signupEmailVerification'
  | 'dashboardSetting'
  | 'surfaceCommentsPerPost'
  | 'postComposerV2'
  | 'postComposerV2Animation'
  | 'onboardingPanel'
  | 'galleryTemplates'
  | 'guidedTemplates'
  | 'dragAndDropV2'
  | 'layoutFormatSettingV2'
  | 'templatesWithNewSidebar'
  | 'classlinkSso'
  | 'surfaceCommenterRole'
  | 'surfacePdfExportPanelV2'
  | 'surfacePostProps'
  | 'surfacePostPropsColor'
  | 'surfacePostPropsLocation'
  | 'surfacePostPropsSubjectAttachmentBody'
  | 'surfacePostPropsCustomDateField'
  | 'surfacePostPropsCustomNumberField'
  | 'surfacePostPropsCustomTextField'
  | 'surfacePostPropsAttachmentSettings'
  | 'editThumbnail'
  | 'virtualizedDashboard'
  | 'commentModeration'
  | 'googleAppLicensing'
  | 'rostering'
  | 'cleverSso'
  | 'dashboardSpeed'
  | 'freezePadlet'
  | 'stripeCheckout'
  | 'fullClientSurface'
  | 'transferPadlets'
  | 'tieredQuotas'
  | 'libraryAnalyticsSettings'
  | 'contentPickerV2'
  | 'annualPlansOnly'
  | 'delinquentChurnBanner'
  | 'personalPricingPlansExperiment'
  | 'suggestedTemplates'
  | 'dashboardUpgradeCta'
  | 'surfaceCustomPropsSorting'
  | 'surfacePostPropsReordering'
  | 'keyboardShortcutsSetting'
  | 'remakeLink'

function isAppUsing(feature: CurrentFlags, version?: null | number): boolean {
  const ww = window.ww || {}
  const flip = ww.flip || ww.vueStartingState?.flip || {}
  if (version) {
    return flip[feature] >= version
  }
  return flip[feature] || false
}

export const isDebugMode =
  environment === 'development' ||
  (environment === 'test' && ['true', 'yes', '1'].includes(process.env.WW_DEBUG ?? '')) ||
  isAppUsing('debugFrontend') ||
  isStringTruthy(new URLSearchParams(window.location.search).get('ww_debug'))

if (environment !== 'production' && isAppUsing('debugFrontend')) {
  console.log('Already debugging frontend in dev.') // goodcheck-disable-line
}

// If on padlet.dev, use the local Banksy server to test PDF exports.
// Banksy and Banksy Dev on Cloud Run won't work because they cannot access a padlet.dev padlet.
// You will need to run the Banksy server first before you can test PDF exports.
// See: /services/banksy/README.md
export const isAppUsingBanksyLocalDev = environment === 'development'

export { isAppUsing }
